import { useMemo } from "react";
import type { Auction, AuctionBid } from "@/services/types";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDateTime } from "@/utils/format";

interface BidTableProps {
  auction: Auction;
  bids: AuctionBid[];
  onWithdraw: (bid: AuctionBid) => void;
  isViewer: boolean;
}

/**
 * The live bid table, sorted lowest-first so the standing leader is unmistakable.
 * Withdrawn bids remain visible — verbal auctions produce mistakes, and a wrongly
 * typed bid must be correctable (not erased) before closing.
 */
export function BidTable({ auction, bids, onWithdraw, isViewer }: BidTableProps) {
  const nameMap = useMemo(() => {
    const map = new Map<string, { name: string; ticket: string }>();
    [...auction.eligible, ...auction.ineligible].forEach((m) => {
      map.set(m.memberId, { name: m.fullName, ticket: m.ticketNumber });
    });
    return map;
  }, [auction]);

  const sorted = useMemo(() => {
    const standing = bids.filter((b) => b.status === "standing").sort((a, b) => a.amount - b.amount);
    const withdrawn = bids.filter((b) => b.status === "withdrawn").sort((a, b) => a.placedAt.localeCompare(b.placedAt));
    return [...standing, ...withdrawn];
  }, [bids]);

  const leaderId = sorted.find((b) => b.status === "standing")?.id;

  if (bids.length === 0) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="border-b border-background-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground-900">Bids placed</h3>
        </div>
        <EmptyState
          compact
          icon="ri-inbox-line"
          title="No bids yet"
          description="Record the first bid to open the auction."
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between border-b border-background-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground-900">Bids placed</h3>
        <span className="tnum text-xs text-foreground-500">{bids.length} total · {bids.filter((b) => b.status === "standing").length} standing</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-background-100/60 text-left text-xs uppercase tracking-wide text-foreground-500">
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2 text-right">Amount to receive</th>
              <th className="px-4 py-2 text-right">Discount</th>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((bid) => {
              const info = nameMap.get(bid.memberId);
              const isLeader = bid.id === leaderId;
              return (
                <tr key={bid.id} className={`border-t border-background-100 ${isLeader ? "bg-accent-50" : bid.status === "withdrawn" ? "opacity-50" : ""}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground-900">{info?.name ?? "Unknown"}</span>
                      <span className="tnum text-xs text-foreground-400">{info?.ticket ?? ""}</span>
                      {isLeader ? <Badge color="accent" dot>Leader</Badge> : null}
                    </div>
                  </td>
                  <td className="tnum px-4 py-2.5 text-right">
                    <CurrencyDisplay amount={bid.amount} hideSymbol className={isLeader ? "font-bold text-accent-900" : ""} />
                  </td>
                  <td className="tnum px-4 py-2.5 text-right text-danger-600">
                    <CurrencyDisplay amount={auction.pool - bid.amount} hideSymbol />
                  </td>
                  <td className="tnum whitespace-nowrap px-4 py-2.5 text-xs text-foreground-500">{formatDateTime(bid.placedAt)}</td>
                  <td className="px-4 py-2.5">
                    {bid.status === "standing" ? (
                      <Badge variant="active">Standing</Badge>
                    ) : (
                      <Badge variant="inactive">Withdrawn</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {bid.status === "standing" && !isViewer ? (
                      <Button size="sm" variant="ghost" icon={<i className="ri-arrow-go-back-line" />} onClick={() => onWithdraw(bid)}>
                        Withdraw
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}